'use client';

import { useEffect, useState } from 'react';

import { OrderApi } from '@ps/api/order';
import { withClientAPI } from '@ps/functions/utils/api';

export interface PurchasedDigitalAsset {
  downloadUrl: string;
}

interface PurchasedDigitalAssetLookup {
  listingId: string;
  asset: PurchasedDigitalAsset | null;
}

/**
 * The signed-in viewer's own purchase of a digital listing, so the PDP can
 * hand a returning buyer their file instead of selling it to them again.
 *
 * Tri-state: `undefined` while the read is in flight, `null` when disabled or
 * the viewer has not bought it, the asset once found. A failed lookup resolves
 * to `null` — the buy CTA is the safe fallback.
 */
export function usePurchasedDigitalAsset(
  listingId: string,
  enabled: boolean,
): PurchasedDigitalAsset | null | undefined {
  const [lookup, setLookup] = useState<PurchasedDigitalAssetLookup | null>(
    null,
  );

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();

    withClientAPI(OrderApi)
      .orderApiControllerGetPurchasedDigitalAsset(
        { listingId },
        { signal: controller.signal },
      )
      .then(response => {
        if (controller.signal.aborted) return;
        setLookup({
          listingId,
          asset: response.asset
            ? { downloadUrl: response.asset.downloadUrl }
            : null,
        });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('getPurchasedDigitalAsset failed', error);
        setLookup({ listingId, asset: null });
      });

    return () => controller.abort();
  }, [listingId, enabled]);

  if (!enabled) return null;
  if (lookup?.listingId !== listingId) return undefined;
  return lookup.asset;
}
